import axios from 'axios';
import { showErrorToast } from '../lib/toast';

// Ensure HTTPS in production
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) {
    // In production, force HTTPS
    if (process.env.NODE_ENV === 'production' && !envUrl.includes('localhost')) {
      return envUrl.replace(/^http:\/\//i, 'https://');
    }
    return envUrl;
  }
  // Default to localhost for development
  return 'http://localhost:8000';
};

const API_BASE_URL = getApiBaseUrl();
const ERROR_TOAST_DEDUPE_MS = 4000;
let lastErrorToastKey = null;
let lastErrorToastAt = 0;

const getGenericErrorMessage = (error) => {
  const status = error?.response?.status;
  if (!status) return 'Unable to reach the server. Please try again.';
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 429) return 'Too many requests. Please wait and try again.';
  if (status >= 500) return 'Something went wrong. If the problem persists, please contact support.';
  return 'Something went wrong. If the problem persists, please contact support.';
};

const maybeShowGlobalErrorToast = (error) => {
  if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') return;
  if (error?.config?.suppressGlobalErrorToast) return;

  const message = getGenericErrorMessage(error);
  const status = error?.response?.status || 'network';
  const method = error?.config?.method || 'unknown';
  const url = error?.config?.url || 'unknown';
  const dedupeKey = `${status}:${method}:${url}`;
  const now = Date.now();

  if (lastErrorToastKey === dedupeKey && now - lastErrorToastAt < ERROR_TOAST_DEDUPE_MS) {
    return;
  }

  lastErrorToastKey = dedupeKey;
  lastErrorToastAt = now;
  showErrorToast(message);
};

// Track ongoing refresh to prevent multiple simultaneous refresh attempts
let refreshPromise = null;

/**
 * Utility function to refresh the access token using the refresh token.
 * This can be used by fetch-based requests that don't use axios interceptors.
 * Prevents multiple simultaneous refresh attempts by reusing an ongoing refresh promise.
 * @returns {Promise<string>} The new access token
 * @throws {Error} If refresh fails
 */
export async function refreshAccessToken() {
  // If a refresh is already in progress, wait for it instead of starting a new one
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      const { access_token, refresh_token: newRefreshToken } = data;

      localStorage.setItem('token', access_token);
      // Update refresh token if a new one is provided (token rotation)
      if (newRefreshToken) {
        localStorage.setItem('refresh_token', newRefreshToken);
      }

      return access_token;
    } catch (error) {
      // Clear tokens on refresh failure
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      throw error;
    } finally {
      // Clear the promise so future refreshes can proceed
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to include the token in headers
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle unauthorized errors and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;

    if (error.response && error.response.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      // Try to refresh token if we have a refresh token
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          // Use the shared refresh function to prevent multiple simultaneous refreshes
          const access_token = await refreshAccessToken();
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          // Refresh failed, clear tokens and redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          // window.location.href = '/login'; // Optional: Redirect to login
          maybeShowGlobalErrorToast(refreshError);
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, clear auth data
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        // window.location.href = '/login'; // Optional: Redirect to login
      }
    }
    maybeShowGlobalErrorToast(error);
    return Promise.reject(error);
  }
);

/**
 * Utility function to make an authenticated fetch request with automatic token refresh.
 * @param {string} url - The URL to fetch
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<Response>} The fetch response
 */
export async function authenticatedFetch(url, options = {}) {
  let token = localStorage.getItem('token');

  // Set authorization header
  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };

  let response = await fetch(url, {
    ...options,
    headers,
  });

  // If we get a 401, try to refresh the token and retry once
  if (response.status === 401) {
    try {
      token = await refreshAccessToken();

      // Retry the request with the new token
      headers['Authorization'] = `Bearer ${token}`;
      response = await fetch(url, {
        ...options,
        headers,
      });
    } catch (refreshError) {
      maybeShowGlobalErrorToast({
        response: { status: response.status },
        config: {
          method: options?.method?.toLowerCase(),
          url,
          suppressGlobalErrorToast: options?.suppressGlobalErrorToast
        }
      });
      // Refresh failed, return the original 401 response
      return response;
    }
  }

  if (!response.ok) {
    maybeShowGlobalErrorToast({
      response: { status: response.status },
      config: {
        method: options?.method?.toLowerCase(),
        url,
        suppressGlobalErrorToast: options?.suppressGlobalErrorToast
      }
    });
  }

  return response;
}

export default apiClient;
